/**
 * Mechanics Config
 * Centralized configuration for passive stat modifiers.
 *
 * This config enables homebrew support by allowing new features to define
 * passive bonuses without modifying characterCalculations.js.
 *
 * Note: Some bonuses have complex conditions (armor-dependent, channeling, etc.)
 * and remain in characterCalculations.js. This config focuses on unconditional
 * or simple conditional bonuses that can be fully config-driven.
 */

import { hasAncestry, hasCommunity, hasSubclass, hasSubclassFeature } from '../utils/featureDetection';
import { hasLoadoutCard } from '../utils/cardFeatureDetection';

/**
 * Passive stat modifiers from ancestry, subclass, and domain cards.
 *
 * Each entry has:
 * - type: stat type ('evasion', 'maxHp', 'maxStress', 'all-thresholds', 'severe-threshold', etc.)
 * - amount: number OR function(character) => number
 * - source: 'ancestry' | 'subclass' | 'domain-card'
 * - featureTier: (optional) 'foundation' | 'specialization' | 'mastery' - for subclass features
 * - replaces: (optional) config key that this feature replaces (for tiered bonuses)
 */
export const PASSIVE_STAT_MODIFIERS = {
  // =========================================================================
  // EVASION BONUSES
  // =========================================================================

  // Simiah Nimble - +1 Evasion (ancestry)
  'simiah:nimble': {
    type: 'evasion',
    amount: 1,
    source: 'ancestry',
  },

  // Untouchable (BONE) - +Agility/2 Evasion (domain card)
  'untouchable': {
    type: 'evasion',
    amount: (char) => Math.floor((char?.traits?.Agility || 0) / 2),
    source: 'domain-card',
  },

  // Shadowhunter (MIDNIGHT) - +1 Evasion in darkness (domain card)
  // Note: Always-on for display, player tracks when in darkness
  'shadowhunter': {
    type: 'evasion',
    amount: 1,
    source: 'domain-card',
  },

  // Nightwalker Fleeting Shadow - +1 Evasion (subclass mastery)
  'nightwalker:fleeting-shadow': {
    type: 'evasion',
    amount: 1,
    source: 'subclass',
    featureTier: 'mastery',
  },

  // =========================================================================
  // DAMAGE THRESHOLD BONUSES
  // =========================================================================

  // Stalwart Unwavering - +1 to all thresholds (subclass foundation)
  'stalwart:unwavering': {
    type: 'all-thresholds',
    amount: 1,
    source: 'subclass',
    featureTier: 'foundation',
  },

  // Stalwart Unrelenting - +2 to all thresholds, replaces Unwavering (subclass specialization)
  'stalwart:unrelenting': {
    type: 'all-thresholds',
    amount: 2,
    source: 'subclass',
    featureTier: 'specialization',
    replaces: 'stalwart:unwavering',
  },

  // Stalwart Undaunted - +3 to all thresholds, replaces Unrelenting (subclass mastery)
  'stalwart:undaunted': {
    type: 'all-thresholds',
    amount: 3,
    source: 'subclass',
    featureTier: 'mastery',
    replaces: 'stalwart:unrelenting',
  },

  // Winged Sentinel Ascendant - +4 to Severe threshold (subclass mastery)
  'winged-sentinel:ascendant': {
    type: 'severe-threshold',
    amount: 4,
    source: 'subclass',
    featureTier: 'mastery',
  },

  // Galapa Shell - +Proficiency to all thresholds (ancestry)
  // Note: Implicit feature - applies automatically from having Galapa ancestry
  'galapa:shell': {
    type: 'all-thresholds',
    amount: (char) => char?.proficiency || 0,
    source: 'ancestry',
    implicitFeature: true,  // Applies just from having the ancestry
  },

  // Rise Up (VALOR) - +Proficiency to Severe threshold (domain card)
  'rise-up': {
    type: 'severe-threshold',
    amount: (char) => char?.proficiency || 0,
    source: 'domain-card',
  },

  // =========================================================================
  // MAX HP BONUSES
  // =========================================================================

  // Giant Endurance - +1 HP (ancestry)
  'giant:endurance': {
    type: 'maxHp',
    amount: 1,
    source: 'ancestry',
  },

  // School of War Battlemage - +1 HP (subclass foundation)
  'school-of-war:battlemage': {
    type: 'maxHp',
    amount: 1,
    source: 'subclass',
    featureTier: 'foundation',
  },

  // =========================================================================
  // MAX STRESS BONUSES
  // =========================================================================

  // Human High Stamina - +1 Stress (ancestry)
  'human:high-stamina': {
    type: 'maxStress',
    amount: 1,
    source: 'ancestry',
  },

  // Vengeance At Ease - +1 Stress (subclass foundation)
  'vengeance:at-ease': {
    type: 'maxStress',
    amount: 1,
    source: 'subclass',
    featureTier: 'foundation',
  },

  // =========================================================================
  // ARMOR SCORE BONUSES
  // Note: Armorer requires armor context, handled in characterCalculations.js
  // =========================================================================
};

// ============================================================================
// TOKEN DEFINITIONS
// Defines tokens/resources that characters can have based on their features.
// This enables homebrew features to define tokens without modifying SimpleTokenService.js.
//
// Each entry has:
// - id: Unique token identifier (used for state tracking)
// - name: Display name
// - type: 'token' | 'dice' | 'resource'
// - source: 'ancestry' | 'community' | 'class' | 'subclass' | 'domain-card'
// - max: number OR function(character) => number
// - refreshTrigger: 'session' | 'rest' | 'long-rest' | 'manual'
// - description: Tooltip text
// - hopeCost: (optional) Hope required to use
// - dieSize: (optional) For dice resources (6, 8, 12, etc.)
// ============================================================================
export const TOKEN_DEFINITIONS = {
  // =========================================================================
  // ANCESTRY TOKENS (examples for homebrew reference)
  // =========================================================================

  'faerie:luckbender': {
    id: 'luckbender-uses',
    name: 'Luckbender',
    type: 'token',
    source: 'ancestry',
    max: 1,
    refreshTrigger: 'session',
    hopeCost: 3,
    description: 'Spend 3 Hope to reroll Duality Dice for yourself or ally within Close range (once per session)',
  },

  'halfling:luckbringer': {
    id: 'luckbringer-uses',
    name: 'Luckbringer',
    type: 'token',
    source: 'ancestry',
    max: 1,
    refreshTrigger: 'session',
    description: 'Grant +1 Hope to all party members (including yourself) at session start',
  },

  // =========================================================================
  // COMMUNITY TOKENS
  // =========================================================================

  'seaborne:tide-tokens': {
    id: 'tide-tokens',
    name: 'Tide Tokens',
    type: 'token',
    source: 'community',
    max: (char) => char?.level || 1,
    startValue: 0, // Starts empty, filled by rolling Fear
    refreshTrigger: 'session',
    description: 'Gain tokens when rolling with Fear, spend for +1 bonus per token to action rolls',
  },

  'orderborne:dedicated': {
    id: 'dedicated-uses',
    name: 'Dedicated',
    type: 'token',
    source: 'community',
    max: 1,
    refreshTrigger: 'rest',
    description: 'Roll d20 as Hope Die when embodying your principles (once per rest)',
  },

  'wanderborne:nomadic-pack': {
    id: 'nomadic-pack-uses',
    name: 'Nomadic Pack',
    type: 'token',
    source: 'community',
    max: 1,
    refreshTrigger: 'session',
    hopeCost: 1,
    description: 'Spend 1 Hope to pull a useful mundane item from your pack (once per session)',
  },

  // =========================================================================
  // CLASS TOKENS
  // =========================================================================

  'bard:rally-die': {
    id: 'rally-die',
    name: 'Rally Die',
    type: 'dice',
    source: 'class',
    max: 1,
    startValue: 0, // Created when bard uses Rally ability
    refreshTrigger: 'session',
    dieSize: (char) => (char?.level >= 5) ? 8 : 6,
    description: (char) => {
      const size = (char?.level >= 5) ? 8 : 6;
      return `d${size} that can be spent to add to action roll, reaction roll, damage roll, or clear Stress equal to result (once per session)`;
    },
  },

  'sorcerer:channel-raw-power': {
    id: 'channel-raw-power',
    name: 'Channel Raw Power',
    type: 'token',
    source: 'class',
    max: 1,
    refreshTrigger: 'session',
    description: 'Sacrifice a domain card: gain +2 Hope from recall OR +Proficiency to next spell damage',
  },

  // =========================================================================
  // SUBCLASS TOKENS
  // =========================================================================

  'call-of-the-slayer:slayer-dice': {
    id: 'slayer-dice',
    name: 'Slayer Dice',
    type: 'dice',
    source: 'subclass',
    max: (char) => char?.proficiency || 2,
    dieSize: 6,
    refreshTrigger: 'session',
    description: 'Add to damage rolls against targets with Mark on them',
  },

  'troubadour:song-tokens': {
    id: 'song-tokens',
    name: 'Song Tokens',
    type: 'token',
    source: 'subclass',
    max: (char) => char?.proficiency || 2,
    refreshTrigger: 'session',
    description: 'Play songs that affect allies in Close range',
    variants: [
      { id: 'relaxing-song', name: 'Relaxing', icon: '🎵' },
      { id: 'epic-song', name: 'Epic', icon: '⚔️' },
      { id: 'heartbreaking-song', name: 'Heartbreaking', icon: '💔' },
    ],
  },

  // =========================================================================
  // SORCERER SUBCLASS TOKENS
  // =========================================================================

  'elemental-origin:transcendence': {
    id: 'transcendence',
    name: 'Transcendence',
    type: 'token',
    source: 'subclass',
    max: 1,
    refreshTrigger: 'long-rest',
    description: 'Transform into elemental form - choose 2 benefits until next rest',
  },

  'primal-origin:enchanted-aid-swap': {
    id: 'enchanted-aid-swap',
    name: 'Enchanted Aid Swap',
    type: 'token',
    source: 'subclass',
    max: 1,
    refreshTrigger: 'rest',
    description: 'Swap ally\'s Duality Dice results after their Spellcast Roll (once per rest)',
  },

  // =========================================================================
  // DOMAIN CARD TOKENS (examples)
  // =========================================================================

  'strategic-approach': {
    id: 'strategic-approach-uses',
    name: 'Strategic Approach',
    type: 'token',
    source: 'domain-card',
    max: 1,
    refreshTrigger: 'rest',
    description: 'Mark Stress: Grant ally advantage on next roll',
  },

  'restoration': {
    id: 'restoration-uses',
    name: 'Restoration',
    type: 'token',
    source: 'domain-card',
    max: 1,
    refreshTrigger: 'rest',
    hopeCost: 3,
    description: 'Touch creature to clear condition or heal 1d6+Prof HP',
  },
};

/**
 * Build a token object from config definition.
 * Handles function-based values for max, dieSize, description.
 *
 * @param {string} configKey - Config key like 'faerie:luckbender'
 * @param {object} config - The config entry from TOKEN_DEFINITIONS
 * @param {object} character - Character object for dynamic values
 * @returns {object} Token object ready for state storage
 */
export const buildTokenFromConfig = (configKey, config, character) => {
  if (!config) return null;

  const max = typeof config.max === 'function' ? config.max(character) : config.max;
  const dieSize = typeof config.dieSize === 'function' ? config.dieSize(character) : config.dieSize;
  const description = typeof config.description === 'function' ? config.description(character) : config.description;
  const startValue = config.startValue !== undefined ? config.startValue : max;

  return {
    id: config.id,
    name: config.name,
    type: config.type,
    current: startValue,
    maximum: max,
    refreshTrigger: config.refreshTrigger,
    description,
    ...(config.hopeCost && { requiresActivation: true, hopeCost: config.hopeCost }),
    ...(dieSize && { dieSize }),
    ...(config.variants && { variants: config.variants }),
    configKey, // Track which config this came from for debugging
  };
};

/**
 * Get tokens from config for a character based on their ancestry/community/class/subclass.
 * This is the config-driven alternative to hardcoded switch statements.
 *
 * @param {object} character - Character object
 * @param {string} sourceFilter - Optional filter: 'ancestry' | 'community' | 'class' | 'subclass' | 'domain-card'
 * @returns {Array} Array of token objects
 */
export const getTokensFromConfig = (character, sourceFilter = null) => {
  const tokens = [];

  if (!character) return tokens;

  for (const [configKey, config] of Object.entries(TOKEN_DEFINITIONS)) {
    // Apply source filter if specified
    if (sourceFilter && config.source !== sourceFilter) continue;

    // Check if character qualifies for this token
    const hasToken = characterQualifiesForToken(character, configKey, config);
    if (!hasToken) continue;

    // Build and add the token
    const token = buildTokenFromConfig(configKey, config, character);
    if (token) {
      tokens.push(token);
    }
  }

  return tokens;
};

/**
 * Check if a character qualifies for a specific token based on config key.
 *
 * @param {object} character - Character object
 * @param {string} configKey - Config key like 'faerie:luckbender' or 'bard:rally-die'
 * @param {object} config - The config entry from TOKEN_DEFINITIONS
 * @returns {boolean} True if character qualifies for this token
 */
export const characterQualifiesForToken = (character, configKey, config) => {
  if (!character || !configKey || !config) return false;

  const { source } = config;

  switch (source) {
    case 'ancestry': {
      // Parse ancestry:feature-name format
      const [ancestryPart] = configKey.split(':');
      if (!ancestryPart) return false;

      // Use feature detection utility for ancestry matching
      return hasAncestry(character, ancestryPart);
    }

    case 'community': {
      // Parse community:feature-name format
      const [communityPart] = configKey.split(':');
      if (!communityPart) return false;

      // Use feature detection utility for community matching
      return hasCommunity(character, communityPart);
    }

    case 'class': {
      // Parse class:feature-name format
      const [classPart] = configKey.split(':');
      if (!classPart) return false;

      // Class check remains inline (no utility needed for simple comparison)
      return character.class?.toLowerCase?.() === classPart;
    }

    case 'subclass': {
      // Parse subclass:feature-name format
      const [subclassPart] = configKey.split(':');
      if (!subclassPart) return false;

      // Use feature detection utility for subclass matching
      return hasSubclass(character, subclassPart);
    }

    case 'domain-card': {
      // Use card feature detection utility for loadout matching
      return hasLoadoutCard(character, configKey);
    }

    default:
      return false;
  }
};

/**
 * Check if a character has a specific feature based on config key.
 *
 * @param {object} character - Character object
 * @param {string} configKey - Config key like 'simiah:nimble' or 'untouchable'
 * @param {object} config - The config entry from PASSIVE_STAT_MODIFIERS
 * @returns {boolean} True if character has this feature
 */
export const characterHasFeature = (character, configKey, config) => {
  if (!character || !configKey) return false;

  const { source, featureTier, implicitFeature } = config;

  switch (source) {
    case 'ancestry': {
      // Parse ancestry:feature-name format
      const [ancestryPart, featurePart] = configKey.split(':');
      if (!ancestryPart || !featurePart) return false;

      // Use feature detection utility for ancestry matching
      if (!hasAncestry(character, ancestryPart)) return false;

      // For implicit features (like Galapa Shell), just having the ancestry is enough
      if (implicitFeature) {
        return true;
      }

      // For explicit features, check ancestryFeatures array
      const ancestryFeatures = character.ancestryFeatures || [];
      const featureNameNormalized = featurePart.replace(/-/g, ' ');
      return ancestryFeatures.some(
        f => f?.name?.toLowerCase?.() === featureNameNormalized ||
             f?.name?.toLowerCase?.().replace(/\s+/g, '-') === featurePart
      );
    }

    case 'subclass': {
      // Parse subclass:feature-name format
      const [subclassPart, featurePart] = configKey.split(':');
      if (!subclassPart || !featurePart) return false;

      // Use feature detection utility for subclass matching
      if (!hasSubclass(character, subclassPart)) return false;

      // Convert feature-name to Title Case for matching
      const featureNameFormatted = featurePart.split('-').map(
        w => w.charAt(0).toUpperCase() + w.slice(1)
      ).join(' ');

      // Use hasSubclassFeature utility for tier checking
      if (featureTier) {
        return hasSubclassFeature(character, featureNameFormatted, featureTier);
      }

      // No tier specified, check all tiers
      return hasSubclassFeature(character, featureNameFormatted);
    }

    case 'domain-card': {
      // Use card feature detection utility for loadout matching
      return hasLoadoutCard(character, configKey);
    }

    default:
      return false;
  }
};

/**
 * Get total passive bonus for a stat type from all applicable features.
 * Handles tiered bonuses (replaces property) correctly.
 *
 * @param {object} character - Character object
 * @param {string} statType - Stat type to get bonus for
 * @returns {number} Total bonus
 */
export const getPassiveBonus = (character, statType) => {
  if (!character) return 0;

  let bonus = 0;
  const appliedKeys = new Set();

  // First pass: collect all applicable features
  const applicableFeatures = [];
  for (const [key, config] of Object.entries(PASSIVE_STAT_MODIFIERS)) {
    // Check if this config applies to the requested stat type
    // Use exact matching - caller is responsible for calling with the right stat type
    // e.g., call getPassiveBonus('all-thresholds') then getPassiveBonus('severe-threshold') separately
    const matches = config.type === statType;

    if (!matches) continue;
    if (!characterHasFeature(character, key, config)) continue;

    applicableFeatures.push({ key, config });
  }

  // Second pass: apply bonuses, handling replacements
  for (const { key, config } of applicableFeatures) {
    // If this feature replaces another, mark the replaced one as not applicable
    if (config.replaces && appliedKeys.has(config.replaces)) {
      // Remove the bonus from the replaced feature
      const replacedConfig = PASSIVE_STAT_MODIFIERS[config.replaces];
      if (replacedConfig) {
        const replacedAmount = typeof replacedConfig.amount === 'function'
          ? replacedConfig.amount(character)
          : replacedConfig.amount;
        bonus -= replacedAmount;
        appliedKeys.delete(config.replaces);
      }
    }

    // Skip if this feature is replaced by another that we've already applied
    if ([...appliedKeys].some(appliedKey => {
      const appliedConfig = PASSIVE_STAT_MODIFIERS[appliedKey];
      return appliedConfig?.replaces === key;
    })) {
      continue;
    }

    // Apply this feature's bonus
    const amount = typeof config.amount === 'function'
      ? config.amount(character)
      : config.amount;

    bonus += amount;
    appliedKeys.add(key);
  }

  return bonus;
};

// ============================================================================
// REACTIVE DAMAGE EFFECTS
// Config-driven effects that trigger when character takes damage.
// Consolidates Warden of the Elements and similar reactive mechanics.
//
// Each entry has:
// - id: Unique effect identifier
// - subclass: (optional) Subclass that grants this effect
// - feature: (optional) Specific feature name required
// - featureTier: (optional) 'foundation' | 'specialization' | 'mastery'
// - condition: Function(character) => boolean - when effect can trigger
// - triggerOn: 'hp-damage' | 'stress-damage' | 'any-damage'
// - effect: Object describing what happens
// - message: Toast message to display
// - icon: Emoji icon for toast
// ============================================================================
export const REACTIVE_DAMAGE_EFFECTS = {
  // =========================================================================
  // WARDEN OF THE ELEMENTS (Druid) - Fire Effects
  // =========================================================================

  'warden-fire-incarnation': {
    id: 'warden-fire-incarnation',
    subclass: 'warden-of-the-elements',
    feature: 'Elemental Incarnation',
    featureTier: 'foundation',
    condition: (char) =>
      char.isChanneling &&
      char.channelingElement === 'fire',
    triggerOn: 'hp-damage',
    effect: {
      type: 'damage-attacker',
      dice: '1d10',
      damageType: 'magic',
    },
    message: (roll) => `Fire Incarnation! Attacker takes ${roll} magic damage!`,
    icon: '🔥',
  },

  'warden-fire-aura': {
    id: 'warden-fire-aura',
    subclass: 'warden-of-the-elements',
    feature: 'Elemental Aura',
    featureTier: 'specialization',
    condition: (char) =>
      char.isChanneling &&
      char.channelingElement === 'fire' &&
      char.elementalAuraActive,
    triggerOn: 'hp-damage',
    effect: {
      type: 'attacker-marks-stress',
      amount: 1,
    },
    message: 'Fire Aura! Attacker marks Stress!',
    icon: '💫',
  },

  // =========================================================================
  // WARDEN OF THE ELEMENTS (Druid) - Water Effects
  // =========================================================================

  'warden-water-incarnation': {
    id: 'warden-water-incarnation',
    subclass: 'warden-of-the-elements',
    feature: 'Elemental Incarnation',
    featureTier: 'foundation',
    condition: (char) =>
      char.isChanneling &&
      char.channelingElement === 'water',
    triggerOn: 'hp-damage',
    effect: {
      type: 'enemies-mark-stress',
      range: 'Melee',
      amount: 1,
    },
    message: 'Water Incarnation! Enemies in Melee mark 1 Stress!',
    icon: '💧',
  },

  'warden-water-dominion': {
    id: 'warden-water-dominion',
    subclass: 'warden-of-the-elements',
    feature: 'Elemental Dominion',
    featureTier: 'mastery',
    condition: (char) =>
      char.isChanneling &&
      char.channelingElement === 'water',
    triggerOn: 'hp-damage',
    effect: {
      type: 'attacker-becomes-vulnerable',
    },
    message: 'Water Dominion! Attacker becomes Vulnerable!',
    icon: '⚡',
  },

  // =========================================================================
  // DOMAIN CARD REACTIVE EFFECTS
  // =========================================================================

  'rise-up-stress-clear': {
    id: 'rise-up-stress-clear',
    domainCard: 'rise-up',
    // Condition checked inline (uses hasRiseUp helper + debounce)
    // Implementation: useVitalsManager.js lines ~197-217
    type: 'inline',
    triggerOn: 'hp-damage',
    effect: {
      type: 'clear-stress',
      amount: 1,
    },
    message: '⬆️ Rise Up: Cleared 1 Stress!',
    icon: '⬆️',
    notes: 'Uses debounce to prevent multiple triggers in same damage instance',
  },

  // =========================================================================
  // CLASS/STATE REACTIVE EFFECTS
  // =========================================================================

  'rogues-dodge-clear': {
    id: 'rogues-dodge-clear',
    classFeature: 'rogue',
    // Condition checked inline (char.roguesDodgeActive)
    // Implementation: useVitalsManager.js lines ~219-225
    type: 'inline',
    triggerOn: 'hp-damage',
    effect: {
      type: 'clear-state',
      key: 'roguesDodgeActive',
    },
    message: "🗡️ Rogue's Dodge ended (hit)",
    icon: '🗡️',
  },

  'beastform-fragile-drop': {
    id: 'beastform-fragile-drop',
    classFeature: 'druid-beastform',
    // Condition checked inline (activeBeastform + Fragile feature + 2+ HP damage)
    // Implementation: useVitalsManager.js lines ~229-240
    type: 'inline',
    triggerOn: 'major-damage', // 2+ HP
    effect: {
      type: 'drop-beastform',
    },
    message: (formName) => `💔 ${formName} dropped from Major damage (Fragile)!`,
    icon: '🐾',
    notes: 'Only triggers if Beastform has Fragile feature',
  },
};

// ============================================================================
// DAMAGE BLOCKING EFFECTS
// Config-driven effects that PREVENT or REDUCE damage before it's applied.
// Different from REACTIVE effects which trigger AFTER damage.
//
// Each entry has:
// - id: Unique effect identifier
// - domainCard: Card that grants this effect
// - condition: Function(character, damageAmount) => boolean
// - blockDamage: { severity: 'minor'|'major'|'severe', or amount: number }
// - message: Toast message when damage is blocked
// - icon: Emoji icon for toast
// ============================================================================
export const DAMAGE_BLOCKING_EFFECTS = {
  'on-the-brink': {
    id: 'on-the-brink',
    domainCard: 'on-the-brink',
    // Implementation: useVitalsManager.js lines ~179-195
    // Condition: hasOnTheBrink(char) && HP <= 2 && damage === 1 (Minor)
    type: 'inline',
    blockDamage: {
      severity: 'minor', // Only blocks 1 HP damage
      condition: (char) => (char.hp || 0) <= 2,
    },
    message: '💀 On the Brink! Minor damage ignored!',
    icon: '💀',
    notes: 'Only blocks when 2 or fewer HP remain',
  },

  'beetles-damage-reduction': {
    id: 'beetles-damage-reduction',
    domainCard: 'beetles',
    // Implementation: useVitalsManager.js lines ~150-177
    // Complex: Requires Hope spending UI, multiple reduction options
    type: 'inline',
    blockDamage: {
      type: 'reduction',
      amountOptions: [1, 2], // 1 HP per Beetle spent
      cost: { type: 'beetle-token' },
    },
    message: '🪲 Beetles absorbed damage!',
    icon: '🪲',
    notes: 'Complex UI - spend Beetle tokens to reduce damage',
  },

  // =========================================================================
  // WARDEN OF THE ELEMENTS (Druid) - Earth Dominion
  // =========================================================================

  'earth-dominion-damage-reduction': {
    id: 'earth-dominion-damage-reduction',
    subclass: 'warden-of-the-elements',
    feature: 'Elemental Dominion',
    featureTier: 'mastery',
    // Implementation: useVitalsManager.js
    // Condition: channeling Earth element + has Elemental Dominion mastery
    type: 'inline',
    blockDamage: {
      type: 'dice-reduction',
      // Roll 1d6 per HP marked, reduce HP by 1 for each 6 rolled
      dicePerHP: 'd6',
      threshold: 6,
      reductionPerSuccess: 1,
    },
    message: (reduced, total, sixes) => `🪨 Earth Dominion! Rolled ${sixes.length} sixes - damage reduced by ${reduced} (${total}→${total - reduced} HP)!`,
    icon: '🪨',
    notes: 'Requires channeling Earth element via Elemental Incarnation',
  },
};

/**
 * Process reactive damage effects for a character taking damage.
 * Called from useVitalsManager when HP damage is taken.
 *
 * @param {Object} character - Character taking damage
 * @param {number} damageAmount - Amount of damage taken
 * @param {Function} hasSubclassFeature - Helper to check subclass features
 * @returns {Array} Array of effect results to process
 */
export const processReactiveDamageEffects = (character, damageAmount, hasSubclassFeature) => {
  const triggeredEffects = [];

  for (const [key, config] of Object.entries(REACTIVE_DAMAGE_EFFECTS)) {
    // Skip inline effects - these are handled directly in useVitalsManager
    if (config.type === 'inline') continue;

    // Check subclass requirement
    if (config.subclass && !hasSubclassFeature(character, config.subclass, config.feature, config.featureTier)) {
      continue;
    }

    // Check condition
    if (config.condition && !config.condition(character)) {
      continue;
    }

    // Check trigger type
    if (config.triggerOn === 'hp-damage' && damageAmount <= 0) {
      continue;
    }

    // Effect triggered - build result
    const result = {
      id: config.id,
      effect: config.effect,
      icon: config.icon,
    };

    // Handle dice-based effects
    if (config.effect.type === 'damage-attacker' && config.effect.dice) {
      const diceMatch = config.effect.dice.match(/(\d*)d(\d+)/);
      if (diceMatch) {
        const count = parseInt(diceMatch[1] || '1');
        const size = parseInt(diceMatch[2]);
        let total = 0;
        for (let i = 0; i < count; i++) {
          total += Math.ceil(Math.random() * size);
        }
        result.roll = total;
        result.message = typeof config.message === 'function'
          ? config.message(total)
          : config.message;
      }
    } else {
      result.message = typeof config.message === 'function'
        ? config.message()
        : config.message;
    }

    triggeredEffects.push(result);
  }

  return triggeredEffects;
};
